import React, { ReactNode, useRef, useEffect } from 'react';
import { Dialog, Grid, Stack, Typography, styled, Box, Fade, IconButton, Slide, alpha } from '@mui/material';
import SbButton from '../Button';
import { Close } from '@mui/icons-material';

interface PropsType {
  icon?: string | ReactNode;
  closeIcon?: boolean;
  title?: string;
  subTitle?: string | ReactNode;
  children?: ReactNode;
  open: boolean;
  btnText?: string;
  onClose: () => void;
  onConfirm?: () => void;
  secondaryBtnText?: string;
  format?: boolean;
}

// Glass morphism backdrop
const GlassBackdrop = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '20px',
  backdropFilter: 'blur(20px)',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.05)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
  pointerEvents: 'none',
}));

// Animated gradient border
// const GradientBorderOverlay = styled(Box)(({ theme }) => ({
//   position: 'absolute',
//   top: 0,
//   left: 0,
//   right: 0,
//   bottom: 0,
//   borderRadius: '20px',
//   padding: '2px',
//   background: `linear-gradient(45deg,
//     ${theme.palette.primary.main},
//     ${theme.palette.secondary.main},
//     ${theme.palette.primary.main})`,
//   backgroundSize: '200% 200%',
//   animation: 'gradientShift 6s ease infinite',
//   WebkitMaskImage: `radial-gradient(ellipse at center,
//   transparent 0%,
//   rgba(0,0,0,0.3) 4px,
//   rgba(0,0,0,0.6) 8px,
//   transparent 100%)`,
//   maskImage: `radial-gradient(ellipse at center,
//   transparent 0%,
//   rgba(0,0,0,0.3) 0px,
//   rgba(0,0,0,0.6) 0px,
//   transparent 100%)`,
//   pointerEvents: 'none',
//   '@keyframes gradientShift': {
//     '0%': { backgroundPosition: '0% 50%' },
//     '50%': { backgroundPosition: '100% 50%' },
//     '100%': { backgroundPosition: '0% 50%' },
//   },
// }));

// Light follow effect
const LightFollowEffect = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '200px',
  height: '200px',
  borderRadius: '50%',
  background: `radial-gradient(circle, 
    ${alpha(theme.palette.primary.main, 0.15)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.05)} 50%, 
    transparent 100%)`,
  filter: 'blur(60px)',
  pointerEvents: 'none',
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 800,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginTop: theme.spacing(2),
  letterSpacing: '-0.02em',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontSize: '1.2rem',
  fontWeight: 400,
}));

const DialogContainer = styled(Box)(({ theme }) => ({
  maxWidth: '640px',
  minWidth: '500px',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: `0 25px 50px -12px ${alpha(theme.palette.primary.main, 0.25)},
              0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.background.default, 0.92)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1.5px solid ${alpha(theme.palette.divider, 0.2)}`,
  position: 'relative',
  '@supports (backdrop-filter: blur(1px))': {
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 0.92)} 0%, 
      ${alpha(theme.palette.background.default, 0.88)} 100%)`,
    backdropFilter: 'blur(20px) saturate(180%)',
  },
}));

const DialogContent = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(5, 4, 4),
  background: 'transparent',
  position: 'relative',
  zIndex: 2,
}));

const Header = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  backgroundSize: '200% 200%',
  height: '120px',
  position: 'relative',
  overflow: 'hidden',
  animation: 'headerGradient 8s ease infinite',
  '@keyframes headerGradient': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${alpha(theme.palette.common.white, 0.8)}, ${alpha(
      theme.palette.secondary.main,
      0.6,
    )}, ${alpha(theme.palette.common.white, 0.4)})`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 3s ease infinite',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.3)}, transparent)`,
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)},
              inset 0 2px 4px ${alpha(theme.palette.common.white, 0.3)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.15)}, ${alpha(
    theme.palette.common.white,
    0.05,
  )})`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  '& img': {
    width: '56px',
    height: '56px',
    objectFit: 'contain',
  },
  '& svg': {
    fontSize: '48px',
    color: theme.palette.common.white,
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  backdropFilter: 'blur(10px)',
  border: `1.5px solid ${alpha(theme.palette.common.white, 0.3)}`,
  color: theme.palette.common.white,
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  zIndex: 10,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    border: `1.5px solid ${alpha(theme.palette.common.white, 0.5)}`,
    transform: 'rotate(90deg) scale(1.1)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '& svg': {
    fontSize: '1.25rem',
  },
}));

// Primary Action Button with magnetic effect
const ActionButton = styled(SbButton)(({ theme }) => ({
  height: '48px',
  minWidth: '160px',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.9375rem',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.95)})`,
  color: theme.palette.primary.contrastText,
  border: `1.5px solid ${alpha(theme.palette.common.white, 0.2)}`,
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha(
    theme.palette.common.white,
    0.2,
  )}`,
  position: 'relative',
  overflow: 'visible',
  cursor: 'pointer',

  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '10px',
    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: -1,
  },

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.4)}, inset 0 1px 0 ${alpha(
      theme.palette.common.white,
      0.3,
    )}`,
    border: `1.5px solid ${alpha(theme.palette.common.white, 0.4)}`,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
  },

  '&:active': {
    transform: 'translateY(-2px) scale(0.98)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha(
      theme.palette.common.white,
      0.2,
    )}`,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(rgba(255, 255, 255, 0.3), transparent)`,
    borderRadius: '10px',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover::after': {
    opacity: 1,
  },
}));

// Secondary Button with enhanced styling
const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
  color: theme.palette.grey[800],
  border: `1.5px solid ${alpha(theme.palette.divider, 0.3)}`,
  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.15)}, inset 0 1px 0 ${alpha(
    theme.palette.common.white,
    0.5,
  )}`,
  backdropFilter: 'blur(8px)',

  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.grey[100]}, ${theme.palette.grey[200]})`,
    color: theme.palette.grey[900],
    boxShadow: `0 12px 24px ${alpha(theme.palette.grey[400], 0.2)}, inset 0 1px 0 ${alpha(
      theme.palette.common.white,
      0.6,
    )}`,
    border: `1.5px solid ${alpha(theme.palette.divider, 0.5)}`,
    transform: 'translateY(-4px)',
  },

  '&::before': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
      theme.palette.secondary.main,
      0.1,
    )})`,
  },
}));

// Sound utility (using Web Audio API)
const playClickSound = (): void => {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContext();

    // Create oscillator for a pleasant click sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Quick frequency sweep for click effect
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1);

    // Quick amplitude envelope
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  } catch (error) {
    // Fallback if audio context fails - silent failure
    console.debug('Audio context unavailable');
  }
};

function Modal({
  open,
  btnText,
  onConfirm,
  onClose,
  icon,
  title,
  subTitle,
  children,
  closeIcon,
  secondaryBtnText,
  format,
}: PropsType): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const lightFollowRef = useRef<HTMLDivElement>(null);

  // Mouse move handler for light follow effect
  useEffect(() => {
    if (!open || !containerRef.current) return;

    const handleMouseMove = (e: MouseEvent): void => {
      if (!containerRef.current || !lightFollowRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Only show light if mouse is over modal
      if (x > 0 && x < rect.width && y > 0 && y < rect.height) {
        lightFollowRef.current.style.opacity = '1';
        lightFollowRef.current.style.left = `${x - 100}px`;
        lightFollowRef.current.style.top = `${y - 100}px`;
      } else {
        if (lightFollowRef.current) {
          lightFollowRef.current.style.opacity = '0';
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [open]);

  const handleConfirm = (): void => {
    playClickSound();
    onConfirm?.();
  };

  const handleClose = (): void => {
    playClickSound();
    onClose();
  };

  const handleSecondaryClick = (): void => {
    playClickSound();
    onClose();
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      scroll={'paper'}
      TransitionComponent={Slide}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <DialogContainer ref={containerRef}>
          {/* Glass morphism backdrop */}
          <GlassBackdrop />

          {/* Animated gradient border */}
          {/* <GradientBorderOverlay /> */}

          {/* Light follow effect */}
          <LightFollowEffect ref={lightFollowRef} />

          {closeIcon && (
            <CloseButton onClick={handleClose} aria-label="close">
              <Close />
            </CloseButton>
          )}

          <Header>
            <IconContainer>
              {icon && typeof icon === 'string' ? <img src={icon} alt="modal icon" /> : icon}
            </IconContainer>
          </Header>

          <DialogContent
            container
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            textAlign="center"
            spacing={2}
          >
            {children ? (
              <Box width="100%">{children}</Box>
            ) : (
              <>
                {title && (
                  <Title variant="h2" className="font-medium">
                    {title}
                  </Title>
                )}

                {subTitle && (
                  <Subtitle variant="body1" className="mt-2 font-normal">
                    {format ? <span style={{ whiteSpace: 'pre-line' }}>{subTitle}</span> : subTitle}
                  </Subtitle>
                )}

                {(btnText || secondaryBtnText) && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    mt={4}
                  >
                    {secondaryBtnText && (
                      <SecondaryButton onClick={handleSecondaryClick} variant="contained">
                        {secondaryBtnText}
                      </SecondaryButton>
                    )}

                    {btnText && (
                      <ActionButton onClick={handleConfirm} variant="contained" color="primary">
                        {btnText}
                      </ActionButton>
                    )}
                  </Stack>
                )}
              </>
            )}
          </DialogContent>
        </DialogContainer>
      </Fade>
    </Dialog>
  );
}

export default Modal;
